/**
 * Report Template — saveable layout configurations for SiteRAG reports.
 */
import type { CompanyArchetype } from './company';
import type { ReportTheme } from './reportTheme';
import type { ReportThemePresetId } from '../lib/reportThemePresets';
import type { ReportTypeId } from '../config/reportTypes';
import {
  resolveStudioSectionList,
  type StudioSectionListMode,
} from '../lib/resolveStudioSectionList';

/** Configuration for a single section in the report. */
export interface SectionConfig {
  sectionId: string;           // headingKey for built-in, or custom ID
  type: 'builtin' | 'custom';
  visible: boolean;
  columnLayout: 1 | 2 | 3;    // how many columns THIS section uses internally
  page?: number;               // which page this section belongs to (1-based)
  columnSpan?: 1 | 2;          // span within a 2-column page layout (1=half, 2=full width)
  customContent?: { title: string; content: string };
  /**
   * TSE-2 (C10) — per-section column override.
   *
   * Differs from `columnLayout` (which counts the columns INSIDE the
   * section): this tells the renderer whether the section honours the
   * page's column count or forces single/double regardless. Useful for
   * BTM tech tables that must run full-width on a 2-column page.
   *   - 'inherit' (default) → use the page's columns
   *   - 'single'            → force 1-col
   *   - 'double'            → force 2-col
   */
  columnOverride?: 'inherit' | 'single' | 'double';
  /** TSE-2 (C6) — distinguishes canonical sections from user-added
   *  custom sections in the unified `sections[]` list. Defaults to
   *  'canonical' on legacy entries (back-compat). */
  kind?: 'canonical' | 'custom';

  // ── TPN (2026-05-18) — page-numbering as a DERIVED value ──────
  /**
   * TPN-3 / TPN-4 — section start-mode for the page-map resolver.
   *
   *  - 'auto'      (default) → derive start page from the cumulative
   *                            content estimate; no page-break is
   *                            forced.
   *  - 'page-pin'            → force this section to start on
   *                            `pagePin`. If `pagePin < cursor` the
   *                            resolver clamps to `cursor` (no
   *                            overlap with prior section); if
   *                            `pagePin > cursor` a whitespace page
   *                            gap is implied. UI surfaces a
   *                            warning chip when content from the
   *                            previous section would overflow the
   *                            pin.
   *  - 'continue'            → render on the same page as the
   *                            previous section ends (no break).
   *
   * Optional — when absent the resolver defaults to 'auto'. Legacy
   * templates carrying the obsolete `pageNumber` field (now removed
   * from the type but tolerated at read time) migrate lazily on the
   * first save: `startMode = 'page-pin'`, `pagePin = pageNumber`.
   */
  startMode?: 'auto' | 'page-pin' | 'continue';

  /**
   * TPN-3 — explicit fixed-page pin (1-based). Only consulted when
   * `startMode === 'page-pin'`. The resolver guarantees the pin
   * never causes an overlap by clamping with `Math.max(pin, cursor)`.
   */
  pagePin?: number;

  /**
   * TPN-2 — heuristic estimate of how many printed pages this
   * section consumes. Stamped by the resolver from the estimator at
   * mount-time / save-time. Optional — when absent the resolver
   * re-computes on the fly via `estimateSectionPages`.
   */
  estimatedPages?: number;
}

/** Page-level layout configuration. */
export interface PageLayout {
  pageNumber: number;
  columns: 1 | 2;             // 1-column or 2-column page layout
  /**
   * TSP-4 — denormalised reverse mapping of `SectionConfig.page` for
   * fast per-page rendering and the PagesTab UI. Optional; when
   * absent, the renderer/UI derives the list at read time from
   * `template.sections.filter(s => s.page === pageNumber)`.
   *
   * Editors that drag-assign a section to a page write BOTH the
   * `SectionConfig.page` field AND this reverse list so the two stay
   * consistent. The renderer uses the section.page field as the
   * source of truth — sectionIds is a display-side convenience.
   */
  sectionIds?: string[];
}

/** Cover layout variants — drives which component renders page 1 of the report.
 *  Mirrored on ReportTheme.coverLayout for tenant-level overrides.
 *
 *  ADDITIVE (report-cover pack): the journal-aesthetic title pages
 *  (`boundary-plate` / `data-snapshot` / `masthead` / `minimal`) extend the
 *  original `classic` / `hero-image` / `data-dense` set. The field stays
 *  OPTIONAL on both ReportTheme and ReportTemplate — an absent value falls
 *  through to the same hero-image default as before, so legacy themes,
 *  templates, and reports render byte-identically with zero migration.
 *  `minimal` already existed (the quiet typographic cover) and is refined,
 *  not renamed. */
export type CoverLayout =
  | 'classic'
  | 'hero-image'
  | 'data-dense'
  | 'minimal'
  | 'boundary-plate'
  | 'data-snapshot'
  | 'masthead'
  /** K5 — additive, opt-in DARK instrument-panel title page (concentric
   *  rings, datum strips, serif display title, verdict chip, KPI rail). Never
   *  a preset default; resolves only on explicit user/template selection. */
  | 'dark-instrument'
  /** Part L (editorial-layouts-l1) — the Editorial-Feature Monograph title page:
   *  series eyebrow, dramatic serif display title, standfirst abstract (the
   *  cover-carried exec summary, in full), KPI rail, on-cover numbered TOC.
   *  Flag-gated (VITE_EDITORIAL_LAYOUTS) — the DEFAULT cover of the `editorial`
   *  preset; never resolves elsewhere with the flag OFF. */
  | 'editorial-masthead'
  /** Report Themes v2 (flag-gated VITE_REPORT_THEMES_V2) — three new title pages,
   *  each the DEFAULT cover of its matching v2 preset and never resolving
   *  elsewhere with the flag OFF (the presets are kept out of the 6-cell matrix
   *  and never default):
   *    - 'prospectus'  → investment-memorandum tombstone (ProspectusCover),
   *                      default cover of the `prospectus` preset;
   *    - 'frontispiece'→ university-press centred title page (FrontispieceCover),
   *                      default cover of the `press` preset;
   *    - 'title-block' → engineering drawing-sheet title block (TitleBlockCover),
   *                      default cover of the `signal` preset. */
  | 'prospectus'
  | 'frontispiece'
  | 'title-block'
  /** Golden convergence (GC-W2) — the investment-MEMO title page (MemoCover):
   *  logo + CONFIDENTIAL/date masthead, tracked memorandum label, sans title +
   *  italic subtitle, SITE/SPONSOR meta, a tier-coloured left-border verdict
   *  box, a 4-up bordered metric strip, DRAFT watermark, BRAND·SITE footer. The
   *  DEFAULT cover of the `memo` preset / the stock default template. */
  | 'memo';

/** Hero-image cover background configuration (template-scoped). */
export interface HeroCoverSettings {
  /**
   * Background source for the hero cover:
   *   - `gradient` (default) — two-stop linear gradient.
   *   - `image`             — uploaded full-bleed photo/SVG.
   *   - `layout-diagram`    — the GENERATED site-layout boundary diagram
   *     (HeroCoverBoundaryOverlay) painted full-bleed. Pure client-side
   *     geometry — no map tiles, no Google Maps URL.
   * All fields below remain OPTIONAL so existing templates render
   * byte-identical (back-compat — see resolveHeroCoverBackground TC1).
   */
  backgroundMode?: 'gradient' | 'image' | 'layout-diagram';
  /** Uploaded hero image URL — used when backgroundMode is `image`. */
  imageUrl?: string;
  /**
   * Alignment of an UPLOADED SVG hero image (`image` mode) within the cover
   * frame, as a 9-position grid. When set AND not `center`, the inlined SVG is
   * rendered with `object-fit: contain` + `object-position` so the artwork
   * keeps its aspect ratio and sits in the chosen position (e.g. a site-grid
   * SVG can sit `bottom-left` instead of being stretched full-bleed).
   *
   * Absence ⇒ the current full-bleed `object-cover` behaviour (back-compat).
   * `center` is treated the same as absence (byte-stable full-bleed default).
   * Only meaningful for uploaded SVG images; ignored for gradient /
   * layout-diagram backgrounds.
   */
  imageAlign?:
    | 'top-left' | 'top-center' | 'top-right'
    | 'center-left' | 'center' | 'center-right'
    | 'bottom-left' | 'bottom-center' | 'bottom-right';
  /** Gradient top colour (defaults to theme.primaryColor). */
  gradientStart?: string;
  /** Gradient bottom colour (defaults to a darkened primary). */
  gradientEnd?: string;
  /**
   * Stroke/line colour for a recoloured hero SVG.
   *
   * Applies to two background sources:
   *   - `layout-diagram` mode — recolours the generated boundary outlines.
   *   - `image` mode where the upload is an SVG — recolours the artwork's
   *     stroke. Because an uploaded SVG is served as an opaque `<img>`/CSS
   *     `url()` and cannot be recoloured directly, the renderer paints it
   *     through a CSS mask using this colour (monochrome recolour). When
   *     unset, the artwork renders as-uploaded.
   */
  heroSvgStrokeColor?: string;
  /**
   * Ordered hex colour stops for a gradient fill applied to a recoloured
   * hero SVG / layout diagram. Two or more stops define a top→bottom
   * linear gradient. When fewer than two stops are supplied the gradient
   * fill is ignored and the stroke colour (or default) applies.
   */
  heroSvgGradient?: string[];
  /**
   * When true, composite the GENERATED site-layout diagram over the chosen
   * background (gradient/image) instead of only in the side panel. Distinct
   * from `backgroundMode === 'layout-diagram'`, which makes the diagram the
   * sole full-bleed background. Defaults to false (legacy side-panel only).
   */
  heroIncludeLayoutDiagram?: boolean;
}

/** A named, saveable report template. */
export interface ReportTemplate {
  id: string;
  name: string;
  /** Where the template lives. 'user' = personal (per-uid). 'company' = shared
   *  with all members of the user's company. Defaults to 'user' on legacy
   *  entries during the first Firestore migration. */
  scope: 'user' | 'company';
  sections: SectionConfig[];
  pageLayouts?: PageLayout[];  // page-level column configuration
  headingOverrides: Record<string, string>;
  isDefault: boolean;
  createdAt: string;
  updatedAt: string;

  // ── Archetype-aware additions ─────────────────────────────────
  /** Optional one-line description (used in the wizard's template
   *  picker so users see what they're choosing). */
  description?: string;
  /** Archetype this template is keyed for. Templates without an
   *  archetype are user-curated layouts that apply uniformly. The
   *  wizard prefers the archetype-matched seed when the site has a
   *  companyArchetype set. */
  defaultArchetype?: CompanyArchetype;
  /**
   * Section-order array (parallel to ReportTypeSections). When set,
   * overrides the type's canonical order at render time. Falls
   * through to `sections.map(s => s.sectionId)` (legacy templates)
   * and then to the type registry. */
  sectionOrder?: string[];
  /** Per-section visibility map. When set, overrides `sections[].visible`. */
  sectionVisibility?: Record<string, boolean>;
  /** Per-section column-layout override. */
  sectionColumnLayouts?: Record<string, 1 | 2 | 3>;
  /**
   * Curated theme preset id — single-column (journal / institutional /
   * monograph) or two-column academic (quarterly / review / proceedings).
   *
   * Sits BELOW `theme` in precedence: the preset supplies the base palette
   * + typography, and any field the user set in `theme` (the template
   * editor) ALWAYS wins over the preset — see `resolveEffectiveTheme`.
   *
   * NEW templates seed `themePreset: DEFAULT_REPORT_THEME_PRESET` (now the
   * two-column 'quarterly' preset). ABSENCE of this field =>
   * render with today's legacy DEFAULT_THEME so existing reports stay
   * byte-stable. OPTIONAL + spread-merged: zero migration on the Firestore
   * round-trip. NEVER auto-restyles existing reports — preset changes reach
   * an existing report only through the version-bump + templateStale
   * re-render prompt (user opt-in).
   */
  themePreset?: ReportThemePresetId;
  /** Theme overrides. Merged INTO the global theme — user fields here
   *  win over the global theme fields, but missing fields fall through. */
  theme?: Partial<ReportTheme>;
  /** Render an inline glossary section in the appendix. False by
   *  default for audiences that don't need acronym definitions. */
  glossaryEnabled?: boolean;
  /** Audience-specific disclaimer — replaces the generic
   *  "Confidential — For Authorized Use Only" line on the cover and
   *  in the DOCX footer. */
  disclaimer?: string;
  /** Cover layout variant — drives which component renders page 1. */
  coverLayout?: CoverLayout;
  /**
   * WS6 — "Scope" block rendered on the DOCX cover under the verdict
   * pill. Three short lines establish reader expectations:
   *   - whatItCovers: 1 sentence describing the report's scope
   *   - howToRead:    1 sentence describing the reading order
   *   - whoItsFor:    1 sentence describing the intended audience
   *
   * Optional — falls through to a generic archetype-keyed default in
   * the exporter when absent. The wizard's template editor can
   * override these per-tenant.
   */
  scopeBlock?: {
    whatItCovers: string;
    howToRead: string;
    whoItsFor: string;
  };

  /**
   * TOR (2026-05-18) — when true (default for new + existing
   * templates), the report viewer projects the report's gen-time
   * section ids through the live `sections[]` order at render time.
   * That lets template re-orders in Studio (drag-reorder, Pages-tab
   * re-assignment) propagate to already-generated reports' TOC + body
   * section numbering without regenerating the report. Content stays
   * frozen at gen-time — only ORDER tracks the template, which
   * preserves the reproducibility contract.
   *
   * Set `false` to PIN this template into immutable ordering (e.g.
   * archived report sets where the customer signed off on a specific
   * § N ordering). Legacy templates without the field default to
   * `true` per the `resolveSectionOrder` opt-out semantics — see
   * src/lib/reportOrdering.ts.
   *
   * Existing reports without a linked `templateId` fall through to
   * the resolver's `report-fallback` branch regardless of this flag —
   * they keep their gen-time order.
   */
  dynamicOrder?: boolean;

  // ── TSE-2 (C12) Version tracking ──────────────────────────────
  /**
   * Monotonic version counter. Bumped each time the template is
   * persisted via `updateReportTemplate`. Existing reports stamp
   * `templateVersionUsed` at gen-time so the viewer can detect
   * "this template has been updated since the report rendered" and
   * prompt the user to re-render or pin.
   * Legacy templates without this field are treated as v1.
   */
  version?: number;

  // ── TSE-3 (P2) Version history ────────────────────────────────
  /**
   * Append-on-Save snapshot ring. Capped at 10 entries — newest at
   * index 0. Restore reads `versionHistory[i].snapshot` back onto the
   * draft template so the user reviews + re-Saves to persist.
   *
   * `snapshot` is stored WITHOUT its own `versionHistory` (we strip
   * before push) so the structure can't recurse — 10 entries each
   * holding 10 more entries would blow up document size at v100.
   *
   * `savedBy` is the writer's uid; `savedByEmail` (optional) lets the
   * UI render "you" vs another teammate without re-fetching users.
   */
  versionHistory?: Array<{
    version: number;
    snapshot: Omit<ReportTemplate, 'versionHistory'>;
    savedAt: string;
    savedBy: string;
    savedByEmail?: string;
  }>;

  // ── TSE-2 (C6) Template-level custom sections ────────────────
  /**
   * User-added text sections rendered between canonical sections.
   * Distinct from per-report `customSections` (which carry generated
   * content per report) — these are TEMPLATE-level inserts that apply
   * to every report rendered with this template.
   *
   *  - `id`           — stable; safe across template edits.
   *  - `title`        — section heading.
   *  - `body`         — plain text (newlines preserved); rendered with
   *                     `whiteSpace: pre-wrap` so it's XSS-safe without
   *                     DOMPurify (no markdown-lite parsing yet — see
   *                     TSE-FOLLOWUP-MARKDOWN-RICH).
   *  - `insertAfter`  — canonical sectionId after which to render.
   *                     `'cover'` is also valid → inserts right after
   *                     the cover.
   *  - `scope`        — `'all'` (every report type) or an explicit
   *                     ReportTypeId[]; renderer filters by current
   *                     report.reportType.
   */
  customSections?: Array<{
    id: string;
    title: string;
    body: string;
    insertAfter: string;
    scope: 'all' | ReportTypeId[];
    /** Optional Gemini instruction block for report generation. */
    generationPrompt?: string;
    /** Optional key in report.sectionContent for AI-generated body. */
    responseField?: string;
  }>;

  /**
   * TSE-2 (C6) Cover overlay block — single optional letter / preface
   *  rendered above the cover layout component. Independent from
   *  `customSections` because it has different rendering rules
   *  (signature line, date line). Renderer falls through when absent.
   */
  coverOverlay?: {
    title: string;
    body: string;
    signature?: string;
    dateLine?: string;
  };

  /**
   * Hero-image cover background — gradient colours or an uploaded
   * full-bleed image. Only read when `coverLayout === 'hero-image'`.
   */
  heroCover?: HeroCoverSettings;

  // ── RTU-4 (2026-05-19, row 0.99235) — per-report-type section list
  /**
   * RTU-4 — Studio Sections / Pages tabs gain a "Configuring: ▾ <type>"
   * dropdown so one template can hold distinct ordered section lists
   * for each report type. Feasibility, Environmental, Test-Fit, etc.
   * each get their own array; cover layout, theme, footer, logo,
   * version, scope block, version history all remain template-wide.
   *
   * Read precedence (see `getEffectiveSections` below):
   *   1. `sectionsByReportType?.[reportType]` — when present, wins.
   *   2. `sections` — flat fallback, treated as the Feasibility list
   *      so legacy templates round-trip without migration.
   *   3. `[]` — empty list (cold-start for an as-yet-unedited type).
   *
   * Optional + sparse: only the types the user has actually edited
   * exist as keys; everything else falls through to the flat
   * `sections` array (or `[]`). Persists via the existing
   * Firestore round-trip; CDS-1 invariant — the saveReportTemplate
   * writer strips undefined so an empty/cleared entry doesn't poison
   * the document.
   */
  sectionsByReportType?: Partial<Record<ReportTypeId, SectionConfig[]>>;
}

/**
 * RTU-4 — resolve the effective ordered section list for a given report
 * type within a template.
 *
 *   1. `template.sectionsByReportType?.[reportType]` — explicit per-type
 *      list wins when present (even if empty — the user explicitly
 *      cleared the list for this type).
 *   2. `template.sections` — flat legacy list. Treated as the
 *      Feasibility default when no per-type list exists so existing
 *      single-list templates render unchanged.
 *   3. `[]` — last-resort empty list (cold-start for a type that has
 *      no per-type list AND no flat fallback). Studio cold-starts
 *      non-Feasibility types into this branch so the Sections tab is
 *      visibly empty until the user explicitly adds entries.
 *
 * Pure read helper — never mutates the template. Writers go through
 * the Studio's `updateDraftTemplate` path which materialises the
 * per-type array lazily on first edit.
 */
export function getEffectiveSections(
  template: ReportTemplate,
  reportType: ReportTypeId,
  options?: { mode?: StudioSectionListMode },
): SectionConfig[] {
  return resolveStudioSectionList({
    template,
    reportType,
    mode: options?.mode ?? 'per-type',
  });
}
